import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Input, Message, Panel } from "rsuite";
import AdminDashboard from "../../components/AdminDashboard";
import { authUserData } from "../../service";
import { AnnouncementPayload, sendAnnouncement } from "../../service/PushNotificationService";

const stripHtml = (html: string) => {
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
};

const AnnouncementPage = () => {
  const authUser = authUserData();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<AnnouncementPayload>({
    announcementType: 1,
    title: "",
    message: ""
  });

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== formData.message) {
      editorRef.current.innerHTML = formData.message;
    }
  }, [formData.message]);

  const toolbarActions = useMemo(
    () => [
      { label: "粗體", command: "bold" },
      { label: "斜體", command: "italic" },
      { label: "底線", command: "underline" },
      { label: "項目清單", command: "insertUnorderedList" },
      { label: "編號清單", command: "insertOrderedList" }
    ],
    []
  );

  const applyCommand = (command: string, value?: string) => {
    if (!editorRef.current) {
      return;
    }
    editorRef.current.focus();
    document.execCommand(command, false, value);
    setFormData((prev) => ({ ...prev, message: editorRef.current?.innerHTML ?? "" }));
  };

  const handleLink = () => {
    const url = window.prompt("請輸入連結網址");
    if (!url) {
      return;
    }
    applyCommand("createLink", url);
  };

  const handleInput = () => {
    setFormData((prev) => ({ ...prev, message: editorRef.current?.innerHTML ?? "" }));
  };

  const handleSubmit = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    const teamCode = authUser?.team?.code?.trim();

    if (!teamCode) {
      setErrorMessage("無法取得登入者的車隊代碼。");
      return;
    }

    if (!formData.title.trim()) {
      setErrorMessage("請輸入公告標題。");
      return;
    }

    if (!stripHtml(formData.message)) {
      setErrorMessage("請輸入公告內容。");
      return;
    }

    setLoading(true);
    const payload: AnnouncementPayload = {
      announcementType: 1,
      title: formData.title.trim(),
      message: formData.message,
      teamCode
    };

    const result = await sendAnnouncement(payload);
    setLoading(false);

    if (!result?.success) {
      setErrorMessage(result?.message || "公告發送失敗，請稍後再試。");
      return;
    }

    setSuccessMessage(result.message || "公告已發送。");
    setFormData((prev) => ({
      ...prev,
      title: "",
      message: ""
    }));
    if (editorRef.current) {
      editorRef.current.innerHTML = "";
    }
  };

  return (
    <AdminDashboard title="公告發送">
      <div className="fleet-compose">
      <Panel bordered header="撰寫公告">
        <div style={{ color: "#64748b", marginBottom: 20 }}>
          使用下方工具設定文字格式。公告會發送給目前登入者所屬車隊。
        </div>

        {errorMessage && (
          <Message showIcon type="error" style={{ marginBottom: 16 }}>
            {errorMessage}
          </Message>
        )}

        {successMessage && (
          <Message showIcon type="success" style={{ marginBottom: 16 }}>
            {successMessage}
          </Message>
        )}

        <Form fluid>
          <Form.Group controlId="teamCode">
            <Form.ControlLabel>發送車隊</Form.ControlLabel>
            <Input value={authUser?.team?.code ?? ""} readOnly plaintext />
          </Form.Group>

          <Form.Group controlId="title">
            <Form.ControlLabel>公告標題</Form.ControlLabel>
            <Input
              value={formData.title}
              onChange={(value) => setFormData((prev) => ({ ...prev, title: value }))}
              placeholder="請輸入公告標題"
            />
          </Form.Group>

          <Form.Group controlId="message">
            <Form.ControlLabel>公告內容</Form.ControlLabel>
            <div className="fleet-editor-toolbar" role="toolbar" aria-label="公告文字格式">
              {toolbarActions.map((action) => (
                <Button
                  key={action.command}
                  size="sm"
                  appearance="ghost"
                  onClick={() => applyCommand(action.command)}
                >
                  {action.label}
                </Button>
              ))}
              <Button size="sm" appearance="ghost" onClick={handleLink}>
                連結
              </Button>
              <Button
                size="sm"
                appearance="ghost"
                style={{ marginRight: 8, marginBottom: 8 }}
                onClick={() => applyCommand("removeFormat")}
              >
                清除格式
              </Button>
            </div>
            <div
              ref={editorRef}
              contentEditable
              onInput={handleInput}
              onBlur={handleInput}
              className="fleet-editor"
              role="textbox"
              aria-label="公告內容"
              aria-multiline="true"
            />
          </Form.Group>

          <Button appearance="primary" loading={loading} disabled={loading} onClick={handleSubmit}>
            發送公告
          </Button>
        </Form>
      </Panel>
      <aside className="fleet-card fleet-compose-preview" aria-label="公告文字預覽">
        <h2>公告文字預覽</h2>
        <small>發送車隊</small><p>{authUser?.team?.name || authUser?.team?.code || '—'}</p>
        <hr /><h3>{formData.title || '尚未輸入公告標題'}</h3>
        <p>{stripHtml(formData.message) || '公告內容將顯示於此，方便在發送前核對文字。'}</p>
        <small>此處僅預覽文字；文字格式與連結請於左側編輯區確認。</small>
      </aside>
      </div>
    </AdminDashboard>
  );
};

export default AnnouncementPage;
